import React, { useState } from "react";
import { Avatar, Button, Grid, Input, Tag } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  EditOutlined,
  LikeFilled,
  LikeOutlined,
  MessageOutlined,
  ShareAltOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  DEFAULT_POST_TYPE,
  POST_TYPE_STYLES,
} from "./postTypes";

const formatDate = (value) =>
  new Date(value).toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
const green = "#006A4E";
const { useBreakpoint } = Grid;

const FeedPostCard = ({
  post,
  commentText,
  onCommentTextChange,
  onCommentSubmit,
  onLikeToggle,
  liking,
  currentUserId,
  onShare,
  onPostEdit,
  onCommentEdit,
  editingPost,
  editingComment,
  readOnly = false,
  commenting,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const currentPostType = post?.postType || DEFAULT_POST_TYPE;
  const typeStyle = POST_TYPE_STYLES[currentPostType] || POST_TYPE_STYLES[DEFAULT_POST_TYPE];
  const likes = Array.isArray(post?.likes) ? post.likes : [];
  const shareCount = Number(post?.shareCount || 0);
  const likedByMe = likes.some((id) => String(id) === String(currentUserId || ""));
  const isMyPost = String(post?.userId?._id || "") === String(currentUserId || "");
  const [editingPostMode, setEditingPostMode] = useState(false);
  const [editingPostText, setEditingPostText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState("");
  const [editingCommentText, setEditingCommentText] = useState("");

  return (
    <div
      style={{
        border: `1px solid ${typeStyle.borderColor}`,
        borderRadius: 12,
        padding: 16,
        background: typeStyle.background,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 10,
          top: 6,
          fontSize: 56,
          opacity: 0.14,
          lineHeight: 1,
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        {typeStyle.icon}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <Avatar
          size={40}
          src={post?.userId?.profilePhoto || undefined}
          icon={<UserOutlined />}
          style={{ background: "#dcefe8", color: green }}
        />
        <div>
          <div style={{ fontWeight: 700, color: "#222", fontSize: 15 }}>
            {post?.userId?.name || "Member"}
          </div>
          <div style={{ color: "#888", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <ClockCircleOutlined />
            {formatDate(post.createdAt)}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Tag
          style={{
            borderColor: `${typeStyle.accent}55`,
            color: typeStyle.accent,
            background: `${typeStyle.accent}12`,
            marginRight: 0,
          }}
        >
          {typeStyle.icon} {currentPostType}
        </Tag>
        {!readOnly && isMyPost && !editingPostMode ? (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingPostMode(true);
              setEditingPostText(post?.text || "");
            }}
          />
        ) : null}
      </div>
      <div style={{ marginTop: 10 }}>
        {editingPostMode ? (
          <div style={{ display: "grid", gap: 8 }}>
            <Input.TextArea
              rows={3}
              value={editingPostText}
              onChange={(e) => setEditingPostText(e.target.value)}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setEditingPostMode(false);
                  setEditingPostText("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                loading={editingPost}
                icon={<CheckOutlined />}
                style={{ background: green, borderColor: green }}
                onClick={() => {
                  const text = String(editingPostText || "").trim();
                  if (!text) return;
                  onPostEdit?.(text, () => {
                    setEditingPostMode(false);
                    setEditingPostText("");
                  });
                }}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ color: "#333", whiteSpace: "pre-wrap" }}>{post.text}</div>
        )}
      </div>

      <div style={{ marginTop: 14, marginBottom: 6 }}>
        <Tag
          color={likedByMe ? "magenta" : "default"}
          style={{ cursor: readOnly ? "default" : "pointer" }}
          onClick={readOnly ? undefined : onLikeToggle}
        >
          {likedByMe ? <LikeFilled /> : <LikeOutlined />} {likes.length} likes
        </Tag>
        <Tag
          color="blue"
          style={{ cursor: "pointer" }}
          onClick={onShare}
        >
          <ShareAltOutlined /> {shareCount} shares
        </Tag>
        <Tag icon={<MessageOutlined />} color="green">
          {(post.comments || []).length} comments
        </Tag>
      </div>

      <div
        style={{
          marginTop: 10,
          paddingTop: 12,
          borderTop: "1px solid #f0f0f0",
          marginLeft: 10,
          marginRight: 10,
          paddingLeft: 6,
          paddingRight: 6,
        }}
      >
        {(post.comments || []).map((comment) => {
          const isMyComment =
            String(comment?.userId?._id || "") === String(currentUserId || "");
          const commentEditMode = editingCommentId === String(comment._id);

          return (
            <div
              key={comment._id}
              style={{
                marginBottom: 8,
                fontSize: 14,
                color: "#444",
                background: "#f8fbfa",
                border: "1px solid #edf4f1",
                borderRadius: 8,
                padding: "7px 9px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#5f6f69" }}>
                    {comment?.userId?.name || "Member"}
                  </span>
                  <span style={{ color: "#8b9893", fontSize: 12, marginLeft: 6 }}>
                    {comment?.createdAt ? formatDate(comment.createdAt) : ""}
                  </span>
                </div>
                {!readOnly && isMyComment && !commentEditMode ? (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingCommentId(String(comment._id));
                      setEditingCommentText(comment?.text || "");
                    }}
                  />
                ) : null}
              </div>

              {commentEditMode ? (
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  <Input.TextArea
                    rows={2}
                    value={editingCommentText}
                    onChange={(e) => setEditingCommentText(e.target.value)}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setEditingCommentId("");
                        setEditingCommentText("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      loading={editingComment}
                      icon={<CheckOutlined />}
                      style={{ background: green, borderColor: green }}
                      onClick={() => {
                        const text = String(editingCommentText || "").trim();
                        if (!text) return;
                        onCommentEdit?.(String(comment._id), text, () => {
                          setEditingCommentId("");
                          setEditingCommentText("");
                        });
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>
                  : {comment.text}
                </div>
              )}
            </div>
          );
        })}

        {!readOnly && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 8,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Input
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => onCommentTextChange(e.target.value)}
              style={{ borderRadius: 8, flex: 1 }}
            />
            <Button
              loading={commenting}
              onClick={onCommentSubmit}
              icon={<SendOutlined />}
            >
              {isMobile ? "" : "Comment"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedPostCard;
